import { useMemo, useRef, useState } from 'react'
import { Tip } from '@ofself/shell'
import { linkedOf } from './model.js'
import { qh } from './qh.js'

/* ═══ SYNONYMS AND ANTONYMS, ON THE PLATE (owner call 2026-10-04) ═══════════
   A link is another WORD, never a string: each chip is a door to it, its ×
   takes the link off both words, and the field adds one — a word already in
   the dictionary picked from the list as you type, or a free word, which the
   server links to the word of that name or mints as a stub. A free word lands
   in the dictionary in force unless another is picked beside the field (a
   Hindi word for an English one: pick Hindi). Both sides are written by the
   server, so nothing here is optimistic: the chip appears when both stand. */
const fold = s => String(s || '').trim().toLowerCase()

export default function Links({ field, word, words = [], dictionaries = [], lens = '', readOnly = false, onLink, onUnlink, onOpen, onNotice }) {
  const kind = field === 'antonyms' ? 'antonym' : 'synonym'
  const linked = linkedOf(word, field, words)
  const [input, setInput] = useState('')
  const [into, setInto] = useState(lens)
  const [busy, setBusy] = useState(false)
  const [hi, setHi] = useState(0)
  const ref = useRef(null)
  // a word already an antonym may be offered as a synonym: linking it moves it (the server's rule)
  const taken = useMemo(() => new Set([word.id, ...(word[field] || []).map(r => r.id)]), [word, field])
  // the words already here that the field could mean: name first, then a name that starts so
  const q = fold(input)
  const offers = q ? words.filter(w => w.name.trim() && !taken.has(w.id) && !w.owner && fold(w.name).includes(q))
    .sort((a, b) => (fold(a.name) === q ? -1 : fold(b.name) === q ? 1 : 0) || (fold(a.name).startsWith(q) ? 0 : 1) - (fold(b.name).startsWith(q) ? 0 : 1) || a.name.localeCompare(b.name))
    .slice(0, 6) : []
  const exact = offers.find(w => fold(w.name) === q)
  const rows = [...offers.map(w => ({ key: w.id, word: w })), ...(q && !exact ? [{ key: '+', term: input.trim() }] : [])]
  const where = t => (t ? t : 'every word')

  const add = async row => {
    if (!row || busy) return
    setBusy(true)
    try {
      const r = await onLink(row.word ? { kind, id: row.word.id } : { kind, term: row.term, ...(into ? { lexicon: into } : {}) })
      setInput(''); setHi(0)
      if (r?.minted) onNotice?.(`${r.target.name} is a new word${into ? ` in ${into}` : ''}${kind === 'synonym' ? ` — it reads ${word.name}’s definition until you give it its own` : ''}.`)
    } catch (e) { onNotice?.(`could not link ${row.word ? row.word.name : row.term}: ${e.message}`) }
    finally { setBusy(false); ref.current?.focus() }
  }
  const drop = async r => {
    try { await onUnlink({ kind, id: r.id, ...(r.group ? { group: r.group } : r.user ? { user: r.user } : {}) }) }
    catch (e) { onNotice?.(`could not unlink ${r.term}: ${e.message}`) }
  }

  return <div className="ofself-prop-chips dt-links">
    {linked.map(r => {
      // a word in another of your dictionaries says which; one in another graph says it is elsewhere
      const other = r.word ? (r.word.tags || []).filter(t => t !== lens) : []
      const note = r.group || r.user ? 'another dictionary' : (lens && r.word && !(r.word.tags || []).includes(lens) ? (other[0] || 'every word') : '')
      const label = <button type="button" className="ofself-prop-chip__label" disabled={!r.word} onClick={() => r.word && onOpen(r.word.id)}>
        {r.term}{note && <span className="dt-links__in"> · {note}</span>}
      </button>
      return <span key={`${r.group || r.user || ''}:${r.id}`} className="ofself-prop-chip">
        <Tip label={r.word ? `Open ${r.term}` : `${r.term} is in a dictionary this page cannot open`}>{label}</Tip>
        {!readOnly && <button type="button" className="ofself-prop-chip__x" aria-label={`Unlink ${r.term}`} onClick={() => drop(r)}>×</button>}
      </span>
    })}
    {!readOnly && <span className="dt-links__add">
      <input ref={ref} className="ofself-prop-chips__in" value={input} disabled={busy}
        placeholder={linked.length ? 'Add…' : kind === 'synonym' ? 'Add a synonym…' : 'Add an antonym…'}
        onChange={e => { setInput(e.target.value); setHi(0) }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown' && rows.length) { e.preventDefault(); setHi(i => (i + 1) % rows.length) }
          else if (e.key === 'ArrowUp' && rows.length) { e.preventDefault(); setHi(i => (i - 1 + rows.length) % rows.length) }
          else if (e.key === 'Enter') { e.preventDefault(); add(rows[hi] || (q ? { term: input.trim() } : null)) }
          else if (e.key === 'Escape' && input) { e.preventDefault(); setInput('') }
        }}
        {...qh(kind === 'synonym' ? 'Add a synonym' : 'Add an antonym', 'Type a word. Pick one already in your dictionary, or press Return to link the word as typed — it becomes a new word if you have none by that name, in the dictionary chosen beside the field.')} />
      {/* the dictionary a free word lands in: the one in force, unless another is picked */}
      {q && !exact && dictionaries.length > 0 && <select className="dt-links__into" value={into} onChange={e => setInto(e.target.value)}
        aria-label="Dictionary a new word lands in" {...qh('Which dictionary', 'A new word is made in the dictionary you are in. Pick another — a Hindi dictionary for a Hindi word — and it is made there, still linked to this one.')}>
        <option value="">Every word</option>
        {dictionaries.map(d => <option key={d.tag} value={d.tag}>{d.tag}{d.anti ? ' (anti)' : ''}</option>)}
      </select>}
      {rows.length > 0 && <span className="dt-pick" role="listbox" aria-label={kind === 'synonym' ? 'Synonyms to link' : 'Antonyms to link'}>
        {rows.map((r, i) => <button key={r.key} type="button" role="option" aria-selected={i === hi} className={`dt-pick__row${i === hi ? ' is-on' : ''}`}
          onMouseDown={e => e.preventDefault()} onClick={() => add(r)}>
          {r.word ? <>{r.word.name}{(r.word.tags || []).length ? <span className="dt-pick__in">{r.word.tags.join(', ')}</span> : null}</>
            : <>Add “{r.term}” <span className="dt-pick__in">new, in {where(into)}</span></>}
        </button>)}
      </span>}
    </span>}
  </div>
}
